import React from "react";
import { Pet } from "../../../common/types";
import { petSvgMap, petSvgMapDark } from "../../helpers/helpers";
import { serverURL } from "../../helpers/utils";

interface ProfileProps {
  totals: {
    totalPets: number;
    totalRewards: number;
    totalScore: number;
    totalFpOwed: number;
    totalFpPerSecond: number;
    totalStars: number;
    totalMonsters: number;
  };
  totalCarrots: number | string;
  totalRewards: number | string;
  totalRewardsInUsd: number | string;
  totalEthBalInUsd: number | string;
  fpBalance: number | string;
  fpBalanceInUsd: number | string;
  totalStakedFp: number | string;
  totalStakedFpInUsd: number | string;
  pGoldBalanceFloat: number | string;
  totalPGoldBalInFp: number | string;
  totalPGoldBalInUsd: number | string;
  ethBalance: number | string;
  walletAddress: string;
  speciesCounts: { species: string; count: number }[] | null;
  pets: Pet[];
  pet: Pet | null;
  isDarkMode: boolean;
}

export const Profile: React.FC<ProfileProps> = ({
  totals,
  totalCarrots,
  totalRewards,
  totalEthBalInUsd,
  fpBalance,
  fpBalanceInUsd,
  totalStakedFp,
  totalStakedFpInUsd,
  pGoldBalanceFloat,
  totalPGoldBalInFp,
  totalPGoldBalInUsd,
  ethBalance,
  walletAddress,
  totalRewardsInUsd,
  speciesCounts,
  pets,
  pet,
  isDarkMode,
}) => {
  const shortenWalletAddress = (address: string) => {
    if (address.length > 12) {
      return `${address.slice(0, 6)}...${address.slice(-6)}`;
    }
    return address;
  };
  const highestScoringPet =
    pets.length > 0
      ? pets.reduce((prev, current) => (prev.score > current.score ? prev : current))
      : null;

  const getPetTypeCounts = (pets: Pet[]) => {
    return pets.reduce((acc: Record<string, number>, pet) => {
      acc[pet.petType] = (acc[pet.petType] || 0) + 1;
      return acc;
    }, {});
  };

  const petTypeCounts = getPetTypeCounts(pets);

  // const totalPercentageChange
  return (
    <div className="mx-auto mb-8 flex w-screen flex-row flex-wrap justify-center overflow-x-hidden">
      <div className="flex-col">
        <div className="flex flex-row">
          {highestScoringPet && highestScoringPet.svgPath && (
            <a
              href={`https://frenpet.xyz/pet/${highestScoringPet.id}`}
              target="_blank"
              rel="noreferrer"
            >
              <div
                className={`ml-4 h-16 w-16 overflow-hidden rounded-md ${isDarkMode ? "bg-darkblack" : "green"} relative`}
              >
                <img
                  src={`${serverURL}/svgs/${highestScoringPet.svgPath}`}
                  alt="Top Pet"
                  className="ml-[.1rem] h-[4.5rem] w-[4.5rem] object-cover"
                />
              </div>
            </a>
          )}
          <h1
            className={`mb-4 ml-10 mt-4 text-xl font-semibold ${isDarkMode ? "text-white" : "text-brown"}`}
          >
            {shortenWalletAddress(walletAddress)}
          </h1>
        </div>
        <div className="flex flex-col items-center space-y-2">
          <div
            className={`mb-2 ml-4 mr-4 mt-2  flex h-[27.75rem] w-[21.5rem] flex-col rounded-lg border-2 p-3 ${isDarkMode ? "border-darkgreen bg-darkblue text-white [box-shadow:inset_0-2px_#37b764]" : "border-brown bg-white [box-shadow:inset_0_-2px_#342e2e]"}`}
          >
            <div
              className={`ml-24 text-xl font-semibold ${isDarkMode ? "text-white" : "text-brown"}`}
            >
              Wallet Funds
            </div>
            <div className="flex flex-row">
              <div
                className={`mb-2 ml-4 mt-2 flex h-[6.75rem] w-[11rem] flex-col rounded-lg border-2 p-3 ${isDarkMode ? "border-darkgreen bg-darkblack [box-shadow:inset_0-2px_#37b764]" : "border-brown bg-skyblue [box-shadow:inset_0_-2px_#342e2e]"}`}
              >
                <div className="text-xl">Rewards</div>
                <div className="mr-1 flex flex-row">
                  <img
                    src={`${isDarkMode ? "/imgs/treasureDark.svg" : "/imgs/treasure.svg"}`}
                    alt=""
                    className="mt-[.15rem] h-[1.6rem] w-[1.6rem]"
                  />{" "}
                  <div className="ml-1 mt-[.1rem] flex flex-row text-xl">
                    {parseFloat(totalRewards.toString()).toFixed(3)}
                    <div className="ml-[.15rem] mt-[.3rem] text-sm"> ETH </div>
                  </div>
                </div>
                <div className="flex flex-row">
                  <div className="mr-1 flex flex-row text-darkgrey"> (${totalRewardsInUsd}) </div>
                  {/*
                  <div className="mr-1 flex flex-row text-darkgrey">
                    {" "}
                    (+{pet ? pet.totalRewardsPercentageChange : "0%"}){" "}
                  </div>
                  */}
                </div>
              </div>
              <div
                className={`mb-2 ml-4 mr-2 mt-2 flex h-[6.75rem] w-[8.5rem] flex-col rounded-lg border-2 p-3 ${isDarkMode ? "border-darkgreen bg-darkblack [box-shadow:inset_0-2px_#37b764]" : "border-brown bg-pink [box-shadow:inset_0_-2px_#342e2e]"}`}
              >
                <div className=" text-xl">ETH</div>
                <div className="mr-1 flex flex-row">
                  <img
                    src={`${isDarkMode ? "/imgs/walletDark.svg" : "/imgs/wallet.svg"}`}
                    alt=""
                    className="mt-[.3rem] h-[1.25rem] w-[1.25rem]"
                  />{" "}
                  <div className="ml-1 mt-[.1rem] text-xl">
                    {parseFloat(ethBalance.toString()).toFixed(3)}
                  </div>
                </div>
                <div className="mr-1 flex flex-row text-darkgrey"> (${totalEthBalInUsd}) </div>
              </div>
            </div>
            <div className="flex flex-row">
              <div
                className={`mb-2 ml-4 mt-2 flex h-[6.75rem] w-[10rem] flex-col rounded-lg border-2 p-3 ${isDarkMode ? "border-darkgreen bg-darkblack [box-shadow:inset_0-2px_#37b764]" : "border-brown bg-green [box-shadow:inset_0_-2px_#342e2e]"}`}
              >
                <div className="text-xl">Staked FP</div>
                <div className="mr-1 flex flex-row">
                  <img
                    src={`${isDarkMode ? "/imgs/frogDark.svg" : "/imgs/frog.svg"}`}
                    alt=""
                    className="mt-[.25rem] h-[1.35rem] w-[1.35rem]"
                  />{" "}
                  <div className="ml-1 mt-[.1rem] text-xl">{totalStakedFp} </div>
                </div>
                <div className="mr-1 flex flex-row text-darkgrey"> (${totalStakedFpInUsd}) </div>
              </div>
              <div
                className={`mb-2 ml-4 mr-2 mt-2 flex h-[6.75rem] w-[10rem] flex-col rounded-lg border-2 p-3 ${isDarkMode ? "border-darkgreen bg-darkblack [box-shadow:inset_0-2px_#37b764]" : "border-brown bg-green [box-shadow:inset_0_-2px_#342e2e]"}`}
              >
                <div className=" text-xl">FP</div>
                <div className="mr-1 flex flex-row">
                  <img
                    src={`${isDarkMode ? "/imgs/frogDark.svg" : "/imgs/frog.svg"}`}
                    alt=""
                    className="mt-[.25rem] h-[1.35rem] w-[1.35rem]"
                  />{" "}
                  <div className="ml-1 mt-[.1rem] text-xl">
                    {parseFloat(fpBalance.toString()).toFixed(1)}{" "}
                  </div>
                </div>
                <div className="mr-1 flex flex-row text-darkgrey"> (${fpBalanceInUsd}) </div>
              </div>
            </div>
            <div className="flex flex-row">
              <div
                className={`mb-2 ml-4 mr-2 mt-2 flex h-[8rem] w-[18.5rem] flex-col rounded-lg border-2 p-3 ${isDarkMode ? "border-darkgreen bg-darkblack [box-shadow:inset_0-2px_#37b764]" : "border-brown bg-gold [box-shadow:inset_0_-2px_#342e2e]"}`}
              >
                <div className=" text-xl">pGold</div>
                <div className="mr-1 flex flex-row">
                  <img
                    src={`${isDarkMode ? "/imgs/coin-bagDark.svg" : "/imgs/coin-bag.svg"}`}
                    alt=""
                    className="mt-[.25rem] h-[1.35rem] w-[1.35rem]"
                  />{" "}
                  <div className="ml-1 mt-[.1rem] text-xl">{pGoldBalanceFloat} </div>
                </div>
                <div className="mr-1 flex flex-row text-darkgrey"> ({totalPGoldBalInFp} FP) </div>
                <div className="mr-1 flex flex-row text-darkgrey"> (${totalPGoldBalInUsd}) </div>
              </div>
            </div>
          </div>
          <div
            className={`mb-2 ml-4 mr-4 mt-2  flex h-[35rem] w-[21.5rem] flex-col rounded-lg border-2 p-3 ${isDarkMode ? "border-darkgreen bg-darkblue text-white [box-shadow:inset_0-2px_#37b764]" : "border-brown bg-white [box-shadow:inset_0_-2px_#342e2e]"}`}
          >
            <div className="ml-24 text-xl font-semibold">Pet Information</div>
            <div className="flex flex-row">
              <div
                className={`mb-2 ml-4 mr-2 mt-2 flex h-[5.75rem] w-[7.9rem] flex-col items-center rounded-lg border-2 p-3 ${isDarkMode ? "border-darkgreen bg-darkblack [box-shadow:inset_0-2px_#37b764]" : "border-brown bg-green [box-shadow:inset_0_-2px_#342e2e]"}`}
              >
                <div className="text-xl">Pets</div>
                <div className="mr-2 flex flex-row">
                  <img
                    src={`${isDarkMode ? "/imgs/darkpaw.svg" : "/imgs/paw.svg"}`}
                    alt=""
                    className="mt-[.2rem] h-6 w-6"
                  />{" "}
                  <div className="ml-1 mt-[.1rem] text-xl">{totals.totalPets} </div>
                </div>
              </div>
              <div
                className={`mb-2 ml-2 mr-4 mt-2 flex h-[5.75rem] w-[11rem] flex-col items-center rounded-lg border-2 p-3 ${isDarkMode ? "border-darkgreen bg-darkblack [box-shadow:inset_0-2px_#37b764]" : "border-brown bg-copper [box-shadow:inset_0_-2px_#342e2e]"}`}
              >
                <div className="text-xl">Carrots</div>
                <div className=" mr-2 flex flex-row">
                  <img
                    src={`${isDarkMode ? "/imgs/carrotDark.svg" : "/imgs/carrot.svg"}`}
                    alt=""
                    className="mt-[.2rem] h-6 w-6"
                  />{" "}
                  <div className="ml-1 mt-[.1rem] text-xl">
                    {parseFloat(totalCarrots.toString()).toFixed(0)}
                  </div>
                </div>
              </div>
            </div>
            <div className="flex flex-row">
              <div
                className={`mb-2 ml-4 mr-4 mt-2 flex h-[5rem] w-[18.5rem] flex-col items-center rounded-lg border-2 p-3 ${isDarkMode ? "border-darkgreen bg-darkblack [box-shadow:inset_0-2px_#37b764]" : "border-brown bg-skyblue [box-shadow:inset_0_-2px_#342e2e]"}`}
              >
                <div className="text-xl">Score</div>
                <div className="mr-2 flex flex-row">
                  <img
                    src={`${isDarkMode ? "/imgs/coin-bagDark.svg" : "/imgs/coin-bag.svg"}`}
                    alt=""
                    className="mt-[.25rem] h-[1.35rem] w-[1.35rem]"
                  />{" "}
                  <div className="ml-2 mt-[.1rem] text-xl">
                    {parseFloat(totals.totalScore.toString()).toFixed(0)}{" "}
                  </div>
                  {/*
                  <div className="ml-1 mt-[.2rem] flex flex-row text-darkgrey">
                    ({pet ? pet.scoreChangePercentage : "N/A"}){" "}
                  </div>
                  */}
                </div>
              </div>
            </div>
            <div className="flex flex-row">
              <div
                className={`mb-2 ml-4 mr-4 mt-2 flex h-auto w-[21.5rem] flex-col items-center rounded-lg border-2 p-3 ${isDarkMode ? "border-darkgreen bg-darkblack text-white [box-shadow:inset_0_-2px_#37b764]" : "border-brown bg-green text-brown [box-shadow:inset_0_-2px_#342e2e]"}`}
              >
                <div className="mb-2 text-xl">Pet Types</div>
                <div className="flex flex-wrap justify-center gap-x-8 gap-y-4">
                  {Object.keys(petSvgMap)
                    .sort((typeA, typeB) => {
                      const countA = speciesCounts?.find((s) => s.species === typeA)?.count || 0;
                      const countB = speciesCounts?.find((s) => s.species === typeB)?.count || 0;
                      return countB - countA;
                    })
                    .map((type) => {
                      const count = petTypeCounts[type] || 0;
                      const iconSrc = isDarkMode ? petSvgMapDark[type] : petSvgMap[type];
                      return (
                        <div key={type} className="flex items-center space-x-2">
                          {iconSrc ? (
                            <img src={iconSrc} alt={type} className="h-6 w-6" />
                          ) : (
                            <span className="text-lg">{type}</span>
                          )}
                          <span className="text-xl">{count}</span>
                        </div>
                      );
                    })}
                </div>
              </div>
            </div>
            <div className="flex flex-row">
              <div
                className={`mb-2 ml-4 mr-1 mt-2 flex h-[5.75rem] w-[9.5rem] flex-col items-center rounded-lg border-2 p-3 ${isDarkMode ? "border-darkgreen bg-darkblack [box-shadow:inset_0-2px_#37b764]" : "border-brown bg-skyblue [box-shadow:inset_0_-2px_#342e2e]"}`}
              >
                <div className="text-xl">Monsters</div>
                <div className="mr-2 flex flex-row">
                  <img
                    src={`${isDarkMode ? "/imgs/pokeballDark.svg" : "/imgs/pokeball.svg"}`}
                    alt=""
                    className="mt-[.25rem] h-6 w-6"
                  />{" "}
                  <div className="ml-1 mt-[.1rem] text-xl">{totals.totalMonsters} </div>
                </div>
              </div>
              <div
                className={`mb-2 ml-2 mr-4 mt-2 flex  h-[5.75rem] w-[9.5rem] flex-col items-center rounded-lg border-2 p-3 ${isDarkMode ? "border-darkgreen bg-darkblack [box-shadow:inset_0-2px_#37b764]" : "border-brown bg-gold [box-shadow:inset_0_-2px_#342e2e]"}`}
              >
                <div className="text-xl">Stars</div>
                <div className="mr-2 flex flex-row">
                  <img
                    src={`${isDarkMode ? "/imgs/starDark.svg" : "/imgs/star.svg"}`}
                    alt=""
                    className="mt-[.2rem] h-6 w-6"
                  />{" "}
                  <div className="ml-1 mt-[.1rem] text-xl">{totals.totalStars} </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
